import {ElasticParser, ElasticSearchProperty} from '../factory.parsers';
import {numberExp, numberGtExp, numberLtExp, NumberParser, numberRangeExp} from './number.parser';
import {defaultRaDecRadius, RaDecParserParser, radiusExp} from './ra-dec.parser';

const numberRangeAllExp = `(${numberRangeExp}|${numberGtExp}|${numberLtExp})`;
export const galacticRangeRegExp = new RegExp(`^${numberRangeAllExp}\\s+${numberRangeAllExp}$`);
export const galacticNoRangeRegExp = new RegExp(`^${numberExp}\\s+${numberExp}\\s*${radiusExp}$`);

export class GalacticParser implements ElasticParser {
  parseValue(galacticString: string, elasticSearchProperty: ElasticSearchProperty): any {
    galacticString = galacticString?.trim();

    const {property} = elasticSearchProperty;
    const isGalacticStringAGalacticRangeRegExp = galacticRangeRegExp.test(galacticString);
    const isGalacticStringAGalacticNoRangeRegExp = galacticNoRangeRegExp.test(galacticString);

    if (isGalacticStringAGalacticRangeRegExp) {
      return this.parseGalacticRangeRegExp(galacticString, elasticSearchProperty);
    } else if (isGalacticStringAGalacticNoRangeRegExp) {
      return this.parseGalacticNoRangeRegExp(galacticString, property);
    } else {
      return undefined;
    }
  }

  private parseGalacticRangeRegExp(galacticString: string, elasticSearchProperty: ElasticSearchProperty): any {
    const match = galacticRangeRegExp.exec(galacticString);

    const indexLongitudeRangeInMatches = 1;
    const indexLatitudeRangeInMatches = 22;

    const longitudeString = match[indexLongitudeRangeInMatches];
    const latitudeString = match[indexLatitudeRangeInMatches];

    const longitudeNumberParser = new NumberParser();
    const longitudeProperty = elasticSearchProperty.property.longitude;
    const longitudeStringParsed = longitudeNumberParser.parseValue(longitudeString, {property: longitudeProperty});

    const latitudeNumberParser = new NumberParser();
    const latitudeProperty = elasticSearchProperty.property.latitude;
    const latitudeStringParsed = latitudeNumberParser.parseValue(latitudeString, {property: latitudeProperty});

    return {bool: {must: [longitudeStringParsed, latitudeStringParsed]}};
  }

  private parseGalacticNoRangeRegExp(galacticString: string, property): any {
    const match = galacticNoRangeRegExp.exec(galacticString);

    const indexLongitudeInMatches = 1;
    const indexLatitudeInMatches = 5;
    const indexRadiusInMatches = 9;

    const longitudeString = match[indexLongitudeInMatches];
    const latitudeString = match[indexLatitudeInMatches];
    const radiusString = match[indexRadiusInMatches];

    const longitudeNumeric = +longitudeString;
    const latitudeNumeric = +latitudeString;
    const radiusNumeric = +radiusString?.replace(',', '') || defaultRaDecRadius;

    const longitudeLatitudeToRaDegDegreesFormatter = new LongitudeLatitudeToRaDegDegreesFormatter();
    const {ra, dec} = longitudeLatitudeToRaDegDegreesFormatter.apply(longitudeNumeric, latitudeNumeric);

    const raDecRadiusProperty = {ra: property.ra, dec: property.dec, footprint: property.footprint};
    const raDecRadiusString = `${ra} ${dec}, ${radiusNumeric}`;

    const decParserParser = new RaDecParserParser();
    return decParserParser.parseValue(raDecRadiusString, {property: raDecRadiusProperty});
  }
}

export class LongitudeLatitudeToRaDegDegreesFormatter {
  private readonly longitudeLatitudeConstants = [
    -0.054875539726, 0.494109453312, -0.867666135858,
    -0.873437108010, -0.444829589425, -0.198076386122,
    -0.483834985808, 0.746982251810, 0.455983795705
  ];

  apply(longitude: number, latitude: number): any {
    const d2r = Math.PI / 180;
    const r2d = 180 / Math.PI;
    const twopi = 2 * Math.PI;

    longitude *= d2r;
    latitude *= d2r;

    const cp1 = Math.cos(latitude);
    const m = [Math.cos(longitude) * cp1, Math.sin(longitude) * cp1, Math.sin(latitude)];

    const s = [0, 0, 0];
    s[0] = m[0] * this.longitudeLatitudeConstants[0] + m[1] * this.longitudeLatitudeConstants[1] + m[2] * this.longitudeLatitudeConstants[2];
    s[1] = m[0] * this.longitudeLatitudeConstants[3] + m[1] * this.longitudeLatitudeConstants[4] + m[2] * this.longitudeLatitudeConstants[5];
    s[2] = m[0] * this.longitudeLatitudeConstants[6] + m[1] * this.longitudeLatitudeConstants[7] + m[2] * this.longitudeLatitudeConstants[8];

    const r = Math.sqrt(s[0] * s[0] + s[1] * s[1] + s[2] * s[2]);
    const b = Math.asin(s[2] / r);
    const cb = Math.cos(b);
    let a = Math.atan2(((s[1] / r) / cb), ((s[0] / r) / cb));

    if (a < 0) {
      a += twopi;
    }

    return {ra: a * r2d, dec: b * r2d};
  }
}
